import type { EditingPayload } from './editingTypes'

//
// The edit locks this browser holds, asked for again on every (re)connect.
//
// The server keeps its lock table in memory. After a restart it is empty, and
// after a dropped connection the server has released whatever this socket held
// — while every open screen still showed "mine". Somebody else could take the
// record, and the first person only found out when saving ended in 409.
//
// So every screen's request is remembered until the screen closes, and sent
// again whenever the socket connects. Whoever asks first gets the lock back;
// the other one is told, exactly as when opening a record somebody else holds.
//
// Free of browser globals on purpose, so a test can drive it against a real
// socket.io server that restarts.
//

type TLockSocket = {
    on(event: 'connect', listener: () => void): unknown
    emit(event: string, payload: unknown): unknown
}

const keyOf = (p: EditingPayload) => `${p.tabId}|${p.table}|${p.id}`

export function createLockKeeper(socket: TLockSocket) {
    const held = new Map<string, EditingPayload>()

    const send = (payload: EditingPayload) => {
        socket.emit('editing client: join', { table: payload.table, id: payload.id })
        socket.emit('editing client: setEditing', payload)
    }

    // Asking twice for a lock one already holds or waits for changes nothing on
    // the server, so the first connect — when the screen's own request may
    // still be buffered — needs no special case.
    socket.on('connect', () => {
        for (const payload of held.values()) send({ ...payload, time: undefined })
    })

    return {
        emit(payload: EditingPayload) {
            if (payload.mode === 'open') held.set(keyOf(payload), payload)
            else held.delete(keyOf(payload))
            send(payload)
        },
    }
}
